import "./styles.scss";
import { features } from './features';

import Main from "./Main.elm";
import Bugsnag from "@bugsnag/js";
import { start, done } from "nprogress";
import "bootstrap";
import {
    EventStreamContentType,
    fetchEventSource,
} from "@microsoft/fetch-event-source";
import {
    registerCustomDrawModes,
    registerCustomElements,
    registerPorts,
} from "./elm-mapbox";
import {
    CircleMode,
    DragCircleMode,
    SimpleSelectMode,
    DirectMode,
} from "./mapbox-gl-draw-circle";
import { registerTripVideo } from "./trip-video/index";
import { heicTo } from "heic-to";
import type { StorageAdapter } from 'featuredrop';
import { createChangelogRenderer, type ChangelogRendererState } from 'featuredrop/renderer'

function loadStorage(key: string) {
    const str = localStorage.getItem(key);
    if (str != null) return JSON.parse(str);
    else return null;
}

const session = loadStorage("session");
const bugsnagConfig = {
    apiKey: "54d271657fb148b00bc91d9b42783a79",
    enabledReleaseStages: ["production", "test"],
    releaseStage: import.meta.env.VITE_ENVIRONMENT,
    appVersion: APP_VERSION,
};

Bugsnag.start({
    ...bugsnagConfig,
    user: { id: session?.user?.accountId, email: session?.user?.email },
});

// Register elm-mapbox custom elements
registerCustomElements({
    token: "pk.eyJ1Ijoic3RlcGhlbndpbnRlciIsImEiOiJjbW1peHBiNmYxNThvMnNzNWdvaHJzYWs2In0.RsBIE5FoydPcFgsW7qct3g",
});

registerCustomDrawModes({
    direct_select: DirectMode,
    simple_select: SimpleSelectMode,
});

// configure FeatureDrop
//
// The bundled LocalStorageAdapter treats the watermark as server-owned and
// read-only, so its dismissAll() only clears per-feature dismissals and never
// marks everything read — "Dismiss all" appears to do nothing. This adapter
// persists both the dismissed set and a mutable watermark to localStorage.
class FeatureDropStorage implements StorageAdapter {
    private watermark: string | null;
    private dismissed: Set<string>;
    private readonly watermarkKey = "fd_:watermark";
    private readonly dismissedKey = "fd_:dismissed";

    constructor() {
        this.watermark = localStorage.getItem(this.watermarkKey);
        const raw = localStorage.getItem(this.dismissedKey);
        const parsed: unknown = raw ? JSON.parse(raw) : [];
        this.dismissed = new Set(Array.isArray(parsed) ? (parsed as string[]) : []);
    }

    getWatermark(): string | null {
        return this.watermark;
    }

    getDismissedIds(): ReadonlySet<string> {
        return this.dismissed;
    }

    dismiss(id: string): void {
        this.dismissed.add(id);
        localStorage.setItem(this.dismissedKey, JSON.stringify([...this.dismissed]));
    }

    async dismissAll(now: Date): Promise<void> {
        this.watermark = now.toISOString();
        this.dismissed.clear();
        localStorage.setItem(this.watermarkKey, this.watermark);
        localStorage.setItem(this.dismissedKey, "[]");
    }
}

const renderer = createChangelogRenderer({
    manifest: features,
    storage: new FeatureDropStorage(),
});

// Feed the current route to the renderer so `{ type: "page" }` triggers work.
// Browser.application navigates via history.pushState (no popstate), so patch it.
function watchPath(onChange: (path: string) => void) {
    const fire = () => onChange(location.pathname);
    for (const method of ["pushState", "replaceState"] as const) {
        const original = history[method].bind(history);
        history[method] = (data: unknown, unused: string, url?: string | URL | null) => {
            original(data, unused, url);
            fire();
        };
    }
    window.addEventListener("popstate", fire);
    fire();
}

watchPath((path) => renderer.actions.setTriggerPath(path));

// The renderer's state holds Sets (dismissedIds, dependencyState.*, triggerContext.*),
// which Elm's decoders can't read — flatten them to arrays before crossing the port.
function toArray(value: unknown): string[] {
    if (value instanceof Set) return Array.from(value as Set<string>);
    return Array.isArray(value) ? (value as string[]) : [];
}

function encodeFeatureDropState(state: ChangelogRendererState) {
    const { dependencyState, triggerContext } = state;
    return {
        ...state,
        dismissedIds: toArray(state.dismissedIds),
        dependencyState: {
            seenIds: toArray(dependencyState?.seenIds),
            clickedIds: toArray(dependencyState?.clickedIds),
            dismissedIds: toArray(dependencyState?.dismissedIds),
        },
        // null, not undefined: an `undefined`-valued key still counts as
        // "present" to Elm's optional-field decoding and breaks the whole decode.
        triggerContext: triggerContext
            ? {
                  ...triggerContext,
                  events: toArray(triggerContext.events),
                  milestones: toArray(triggerContext.milestones),
              }
            : null,
    };
}


const apiUrl = import.meta.env.VITE_API_URL;
const newApiUrl = import.meta.env.VITE_NEW_API_URL;
const fleetUrl = import.meta.env.VITE_FLEET_URL;
const app = Main.init({
    flags: {
        session: session,
        apiUrl: apiUrl,
        newApiUrl: newApiUrl,
        useNewServer: loadStorage("useNewServer") ?? false,
        fleetUrl: fleetUrl,
        defaultMapPosition: loadStorage("defaultMapPosition"),
        selectedFleetId: loadStorage("selectedFleetId"),
        language: navigator.language,
        showedNavigationWarning: loadStorage("showedNavigationWarning"),
        bugsnagConfig: bugsnagConfig,
        featureDropState: encodeFeatureDropState(renderer.state)
    },
});

// Register elm-mapbox ports
registerPorts(app);

// Register Trip video
registerTripVideo();

interface NeboTripVideoElement extends HTMLElement {
    tripData: unknown;
    config: unknown;
    play(): void;
    pause(): void;
    seekTo(frame: number): void;
    startExport(): Promise<void>;
}

let abortController = new AbortController();

app.ports.save?.subscribe((data) => {
    if (data[1] === null) {
        localStorage.removeItem(data[0]);
    } else {
        localStorage.setItem(data[0], JSON.stringify(data[1]));
    }
    if (data[0] === "session") {
        Bugsnag.setUser(data[1]?.user?.accountId, data[1]?.user?.email);
    }
});

app.ports.startProgressBar?.subscribe(() => setTimeout(start, 100)); // Ensure this runs after the initial page render
app.ports.completeProgressBar?.subscribe(() => done(false));

class RetriableError extends Error { }
class ServerError extends Error {
    constructor(
        public status: number,
        public message: string,
    ) {
        super(message);
    }
}

app.ports.requestEventStream?.subscribe((data) => {
    abortController.abort(); // abort previous fetch
    abortController = new AbortController();
    console.log(`Requesting server event stream: ${data[0]}`);
    fetchEventSource(data[0], {
        headers: {
            Authorization: `Bearer ${data[1]}`,
            Accept: "text/event-stream",
        },
        signal: abortController.signal,
        async onopen(response) {
            if (
                response.ok &&
                response.headers
                    .get("content-type")
                    ?.includes(EventStreamContentType)
            ) {
                return; // everything's good
            } else if (
                response.status >= 400 &&
                response.status < 500 &&
                response.status !== 429
            ) {
                throw new ServerError(response.status, response.statusText);
            } else {
                throw new RetriableError();
            }
        },
        onmessage(ev) {
            console.log(`Received server event: ${ev.event}`);
            app.ports.receiveServerEvent?.send({
                id: ev.id,
                data: ev.data,
                event: ev.event,
            });
        },
        onerror(err) {
            console.log(`Server event stream returned an error: ${err}`);
            if (err instanceof ServerError) {
                app.ports.serverEventConnectionError?.send([
                    err.status,
                    err.message,
                ]);
                throw err;
            } else if (
                err instanceof DOMException &&
                err.name === "AbortError"
            ) {
                console.log("Server event stream was aborted");
                throw err;
            } else {
                // Log the error & retry
                Bugsnag.notify(err);
            }
        },
        onclose() {
            console.log("Server event stream closed");
        },
    });
});

app.ports.cancelEventStream?.subscribe(() => {
    console.log(`Cancelling server event stream`);
    abortController.abort();
    abortController = new AbortController();
});

app.ports.setAnalyticsParameters?.subscribe((data) => {
    let attrs: any = {};
    for (let [key, value] of data) {
        attrs[key] = value;
    }
    gtag("set", attrs);
});

app.ports.sendPageViewEvent?.subscribe((data) => {
    gtag("event", "page_view", { page_location: data[0], page_title: data[1] });
});

app.ports.copyToClipboard?.subscribe((data) => {
    navigator.clipboard.writeText(data);
});

app.ports.pushUrlSilent?.subscribe((url) => {
    history.pushState(null, "", url);
});

app.ports.videoPlayerCommand?.subscribe((cmd: Record<string, unknown>) => {
    const el = document.querySelector(
        "nebo-trip-video",
    ) as NeboTripVideoElement | null;
    if (!el) return;

    switch (cmd.type) {
        case "play":
            el.play();
            break;
        case "pause":
            el.pause();
            break;
        case "seek":
            el.seekTo(cmd.frame as number);
            break;
        case "export":
            el.startExport();
            break;
        case "download": {
            const a = document.createElement("a");
            a.href = cmd.url as string;
            a.download = "trip-video.mp4";
            a.click();
            break;
        }
    }
});

app.ports.togglePlaceAudio?.subscribe(() => {
    const el = document.getElementById("placeAudio") as HTMLAudioElement | null;
    if (!el) return;
    if (el.paused) {
        el.play().catch((err) => console.log(`Failed to play place audio: ${err}`));
    } else {
        el.pause();
    }
});

app.ports.convertHeicPhoto?.subscribe(async ([key, dataUrl]) => {
    try {
        const sourceBlob = await (await fetch(dataUrl)).blob();
        const jpegBlob = await heicTo({
            blob: sourceBlob,
            type: "image/jpeg",
            quality: 0.9,
        });
        const reader = new FileReader();
        reader.onload = () => {
            app.ports.heicPhotoConverted?.send([
                key,
                reader.result as string,
            ]);
        };
        reader.onerror = () => {
            Bugsnag.notify(new Error(`Failed to read converted HEIC photo: ${key}`));
        };
        reader.readAsDataURL(jpegBlob);
    } catch (err) {
        console.log(`Failed to convert HEIC photo ${key}: ${err}`);
        Bugsnag.notify(err instanceof Error ? err : new Error(String(err)));
    }
});


const featureDropActions = renderer.actions as unknown as Record<
    string,
    (...args: unknown[]) => unknown
>;

// FeatureDrop widget interactions -> Google Analytics events.
const featureDropGaEvent: Record<string, string> = {
    dismiss: "featuredrop_dismiss", // a single feature dismissed
    dismissAll: "featuredrop_dismiss_all", // "Dismiss all" in the header
    markFeatureClicked: "featuredrop_feature_click", // a feature CTA clicked
};

app.ports.featureDropAction?.subscribe(({ method, args }) => {
    const fn = featureDropActions[method];
    if (typeof fn !== "function") {
        Bugsnag.notify(new Error(`Unknown FeatureDrop action: ${method}`));
        return;
    }
    try {
        const result = fn(...(Array.isArray(args) ? args : []));
        if (result instanceof Promise) {
            result.catch((err) =>
                Bugsnag.notify(err instanceof Error ? err : new Error(String(err))),
            );
        }
        const gaEvent = featureDropGaEvent[method];
        if (gaEvent) {
            gtag("event", gaEvent, {
                feature_id: typeof args?.[0] === "string" ? args[0] : undefined,
            });
        }
    } catch (err) {
        console.log(`FeatureDrop action ${method} failed: ${err}`);
        Bugsnag.notify(err instanceof Error ? err : new Error(String(err)));
    }
});

renderer.subscribe((state) => {
    // Elm decodes this; the port's generated type still says ChangelogRendererState.
    app.ports.featureDropState?.send(
        encodeFeatureDropState(state) as unknown as ChangelogRendererState,
    );
});

// Version check
fetch("/server-version.json")
    .then(async (resp) => {
        let server = await resp.json();
        if (server.version != APP_VERSION) {
            console.log(
                `Client is out of date! - server version is ${server.version}`,
            );
            Bugsnag.leaveBreadcrumb("Client is out of date: reloading", {
                serverVersion: server.version,
                clientVersion: APP_VERSION,
            });
            gtag("event", "updating", {
                event_category: "version",
                event_label: APP_VERSION,
            });
            location.reload();
        } else {
            console.log(`Client app version ${APP_VERSION} is up to date`);
        }
    })
    .catch((r) => console.log(`Error loading server-version: ${r}`));
